// UltraFlex Admin — Revenue Recognition Dashboard
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { TrendingUp, Play, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminRevenue() {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sumRes, schRes] = await Promise.all([
        fetch(`${API}/api/revenue/summary`, { headers }),
        fetch(`${API}/api/revenue/schedules`, { headers }),
      ]);
      setSummary(await sumRes.json());
      setSchedules((await schRes.json()).items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const runRecognition = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API}/api/revenue/run-recognition`, { method: 'POST', headers, body: '{}' });
      const data = await res.json();
      alert(`Recognised: ${data.schedules_processed} schedule(s), £${data.total_recognized?.toFixed(2)}`);
      fetchData();
    } catch (e) { console.error(e); }
    setRunning(false);
  };

  return (
    <div className="space-y-6" data-testid="revenue-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Revenue Recognition</h1>
          <p className="text-sm text-gray-400 mt-1">Deferred revenue schedules and monthly recognition</p>
        </div>
        <button onClick={runRecognition} disabled={running} data-testid="run-recognition-btn"
          className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
          <Play className={`w-4 h-4 ${running ? 'animate-pulse' : ''}`} /> Run Recognition
        </button>
      </div>

      {loading ? <div className="text-gray-500 text-center py-8">Loading...</div> : (
        <>
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-[#111] border border-gray-800 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-white">{summary.active_schedules}</div>
                <div className="text-xs text-gray-500 uppercase mt-1">Active Schedules</div>
              </div>
              <div className="bg-[#111] border border-gray-800 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-blue-400">£{summary.total_original_amount?.toFixed(2)}</div>
                <div className="text-xs text-gray-500 uppercase mt-1">Original Amount</div>
              </div>
              <div className="bg-[#111] border border-gray-800 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-green-400">£{summary.total_recognized_to_date?.toFixed(2)}</div>
                <div className="text-xs text-gray-500 uppercase mt-1">Recognised</div>
              </div>
              <div className="bg-[#111] border border-gray-800 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-yellow-400">£{summary.total_deferred_balance?.toFixed(2)}</div>
                <div className="text-xs text-gray-500 uppercase mt-1">Deferred Balance</div>
              </div>
            </div>
          )}

          <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
            <div className="p-3 border-b border-gray-800"><h2 className="text-sm font-medium text-gray-400 uppercase">Deferral Schedules</h2></div>
            <table className="w-full text-sm">
              <thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                <th className="text-left p-3">Site</th><th className="text-right p-3">Total</th>
                <th className="text-right p-3">Monthly</th><th className="text-right p-3">Recognised</th>
                <th className="text-right p-3">Remaining</th><th className="text-left p-3">Period</th>
                <th className="text-center p-3">Status</th>
              </tr></thead>
              <tbody>
                {schedules.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-gray-600">No deferral schedules — schedules are created when prepaid products/memberships are sold</td></tr> :
                  schedules.map(s => (
                    <tr key={s.id} className="border-b border-gray-800/50 hover:bg-gray-900/50">
                      <td className="p-3 text-white">{s.site_name}</td>
                      <td className="p-3 text-right text-gray-300">£{s.total_amount?.toFixed(2)}</td>
                      <td className="p-3 text-right text-gray-300">£{s.monthly_recognition?.toFixed(2)}</td>
                      <td className="p-3 text-right text-green-400">£{s.recognized_to_date?.toFixed(2)}</td>
                      <td className="p-3 text-right text-yellow-400">£{s.remaining_balance?.toFixed(2)}</td>
                      <td className="p-3 text-gray-400 text-xs">{s.start_date} to {s.end_date}</td>
                      <td className="p-3 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded ${s.status === 'active' ? 'bg-green-900/40 text-green-400' : 'bg-gray-800 text-gray-400'}`}>{s.status}</span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
